
import { Component } from 'vue-property-decorator';
import Export from '@/components/export/Export.vue';
import type { Project } from '@/models/project';
import { Action, Getter } from 'vuex-class';
import type { ExtendedTrade } from '@/models/trade';
import { PrineConvertExportData } from '@/lib/prine/convert/types/PrineConvertExportData';
import moment from 'moment';
import { getCombinedProjectUsers } from '@/helpers/getCombinedProjectUsers';
import type { ProjectUser } from '@/models/projectUser';
import { PrineConvert } from '@/lib/prine/convert/PrineConvert';
import { PrineConvertAdapterHTML } from '@/lib/prine/convert/adapters/PrineConvertAdapterHTML';
import type { Company } from '@/models/company';
import ExportLayout from '@/components/export/ExportLayout.vue';
import type { ExtendedPMPhase } from '@/models/pmphase';
import type { XProcess } from '@/models/xprocess';
import { projectWebservice } from '@/webservices/project';
import { showErrorNotification } from '@/helpers/showErrorNotification';
import { topicWebservice } from '@/webservices/topic';
import type { Topic } from '@/models/topic';
import { findCombinedUser } from '@/helpers/findCombinedUser';
import { ResolvedEnum } from '@/helpers/filter/appliedLessons/filterAppliedLessonsResolved';
import { CheckedEnum } from '@/helpers/filter/appliedLessons/filterAppliedLessonsChecked';
import {
    processPmAppliedLessons,
    processTradeAppliedLessons,
    processXAppliedLessons,
} from '@/helpers/filter/appliedLessons/filterAppliedLessons';
import { DynamicField } from '@/models/dynamicField';
import { ExportField } from '@/components/export/ExportField';
import { PMSubPhase } from '@/models/pmsubphase';
import { pmSubPhaseWebservice } from '@/webservices/pmsubphase';
import { PrineConvertDataContainer } from '@/lib/prine/convert/types/PrineConvertDataContainer';

@Component({
    name: 'ExportProjectAppliedLessons1Click',
    components: { ExportLayout },
})
export default class ExportProjectAppliedLessons1Click extends Export {
    get descriptions() {
        const descriptions: string[] = [];
        if (this.dynamicFields.length > 0) {
            descriptions.push(this.companySetting.dynamic_worksheet_field_name + ': ' + this.dynamicFields.join(', '));
        }

        switch (this.filterResolved) {
            case ResolvedEnum.Resolved:
                descriptions.push('Erledigte Aufgaben');
                break;
            case ResolvedEnum.NotResolved:
                descriptions.push('Offene Aufgaben');
                break;
            default:
                descriptions.push('Alle Aufgaben');
                break;
        }

        if (this.topicsSelected.length > 0) {
            descriptions.push('Themenbereiche: ' + this.topicsSelected.map((topic) => topic.title).join(', '));
        }

        if (this.pmSubPhasesSelected.length > 0) {
            descriptions.push(
                'PM Sub Phasen: ' +
                    Array.from(new Set(this.pmSubPhasesSelected.map((subPhase) => subPhase.title))).join(', ')
            );
        }
        descriptions.push('Erstellt am: ' + moment().format('DD.MM.YYYY') + ' ' + moment().format('HH:mm:ss'));
        return descriptions;
    }

    get users() {
        return getCombinedProjectUsers(this.project) as ProjectUser[];
    }

    get trades() {
        if (!this.project || !this.project.trades) {
            return [];
        }
        const clonedTrades = JSON.parse(JSON.stringify(this.project.trades)) as ExtendedTrade[];
        return clonedTrades
            .filter((trade) => this.tradeIds.length === 0 || this.tradeIds.includes(trade.id))
            .map((trade: ExtendedTrade) => {
                trade.phases = trade.phases
                    .filter((phase) => phase.worksheet && phase.worksheet.applied_lessons)
                    .filter((phase) => (this.phaseIds.length > 0 ? this.phaseIds.includes(phase.id) : true))
                    .map((phase) => {
                        phase.worksheet.applied_lessons = processTradeAppliedLessons({
                            appliedLessons: phase.worksheet.applied_lessons,
                            users: this.users,
                            dynamicFields: this.dynamicFields,
                            filterChecked: this.filterChecked,
                            filterResolved: this.filterResolved,
                            topicIds: this.topicIds,
                        });

                        return phase;
                    });

                return trade;
            })
            .filter((trade: ExtendedTrade) => {
                return trade.phases.length !== 0 && trade.phases.find((p) => p.worksheet.applied_lessons.length > 0);
            });
    }

    get transformedTrades(): PrineConvertExportData {
        const reducedActiveFields = this.reducedActiveFields(this.tradeFields);
        return this.trades.map((trade: ExtendedTrade) => {
            return {
                fields: this.translatedFields(reducedActiveFields, { trade }),
                rows: trade.phases.reduce((previousPhase, currentPhase) => {
                    const tempPhase: any = currentPhase.worksheet.applied_lessons.map((appliedLesson) => {
                        return reducedActiveFields.map((field) =>
                            field.data
                                ? field.data({
                                      phase: currentPhase,
                                      appliedLesson,
                                      users: this.users,
                                  })
                                : ''
                        );
                    });

                    if (tempPhase.worksheet && tempPhase.worksheet.applied_lessons.length > 0) {
                        return previousPhase.concat(tempPhase);
                    } else {
                        // return previousPhase;
                        return previousPhase.concat(tempPhase);
                    }
                }, [] as any[]),
            };
        });
    }

    get pmPhases() {
        if (!this.project) {
            return [];
        }
        const clonedPMPhases = JSON.parse(JSON.stringify(this.project.pmphases)) as ExtendedPMPhase[];

        console.log('pm', this.pmPhaseIds);
        return clonedPMPhases
            .filter(
                (pmPhase) => pmPhase.worksheet && (this.pmPhaseIds.length === 0 || this.pmPhaseIds.includes(pmPhase.id))
            )
            .map((pmPhase) => {
                pmPhase.worksheet.pm_applied_lessons = processPmAppliedLessons({
                    appliedLessons: pmPhase.worksheet.pm_applied_lessons,
                    users: this.users,
                    dynamicFields: this.dynamicFields,
                    filterChecked: this.filterChecked,
                    filterResolved: this.filterResolved,
                    pmSubPhasesIds: this.pmSubPhasesIds,
                });

                return pmPhase;
            })
            .filter((pmphase) => {
                return pmphase.worksheet.pm_applied_lessons.length > 0;
            });
    }

    get transformedPmPhases(): PrineConvertExportData {
        const reducedActiveFields = this.reducedActiveFields(this.pmPhaseFields);
        const subPhases =
            this.pmSubPhasesSelected && this.pmSubPhasesSelected.length ? this.pmSubPhasesSelected : this.pmSubPhases;

        const lessonsGroupedByPmSubPhase = this.pmPhases.flatMap((pmPhase) => {
            return subPhases
                .filter((pmSubPhase) => pmSubPhase.pm_phase_id === pmPhase.id)
                .map((pmSubPhase) => {
                    const relevantLessons = pmPhase.worksheet.pm_applied_lessons.filter(
                        (pmAppliedLesson) => pmAppliedLesson.lesson.pm_sub_phase_id === pmSubPhase.id
                    );

                    if (relevantLessons.length === 0) {
                        return null;
                    }

                    return {
                        fields: this.translatedFields(reducedActiveFields, { pmphase: pmPhase, pmSubPhase }),
                        rows: relevantLessons.map((pmAppliedLesson) =>
                            reducedActiveFields.map((field) =>
                                field.data
                                    ? field.data({
                                          pmphase: pmPhase,
                                          pmAppliedLesson,
                                          users: this.users,
                                      })
                                    : ''
                            )
                        ),
                    };
                })
                .filter(Boolean);
        });

        return lessonsGroupedByPmSubPhase as PrineConvertExportData;
    }

    get xProcesses() {
        if (!this.project) {
            return [];
        }
        const clonedXProcesses = JSON.parse(JSON.stringify(this.project.xprocesses)) as XProcess[];

        return clonedXProcesses
            .filter((xProcess) => this.xProcessIds.length === 0 || this.xProcessIds.includes(xProcess.id))
            .map((xProcess) => {
                if (!xProcess.x_worksheet) {
                    return xProcess;
                }

                xProcess.x_worksheet.x_applied_lessons = processXAppliedLessons({
                    appliedLessons: xProcess.x_worksheet.x_applied_lessons,
                    users: this.users,
                    dynamicFields: this.dynamicFields,
                    filterChecked: this.filterChecked,
                    filterResolved: this.filterResolved,
                    xSubProcessesIds: this.xSubProcessesIds,
                });

                return xProcess;
            })
            .filter((xProcess) => {
                return xProcess.x_worksheet && xProcess.x_worksheet.x_applied_lessons.length > 0;
            });
    }

    get transformedXProcesses(): PrineConvertExportData {
        const reducedActiveFields = this.reducedActiveFields(this.xProcessFields);
        return this.xProcesses.map((x_process: XProcess) => {
            return {
                fields: this.translatedFields(reducedActiveFields, { x_process }),
                rows: x_process.x_worksheet
                    ? x_process.x_worksheet.x_applied_lessons.map((x_applied_lessons) => {
                          return reducedActiveFields.map((field) =>
                              field.data
                                  ? field.data({
                                        x_process,
                                        x_applied_lessons,
                                        users: this.users,
                                    })
                                  : ''
                          );
                      })
                    : [],
            };
        });
    }

    get htmlEntriesTrade() {
        const transformed = this.transformedTrades;
        if (transformed && transformed.length > 0) {
            return PrineConvert.export(transformed).using(
                new PrineConvertAdapterHTML({
                    columnStyles: this.columnStyles(this.tradeFields),
                })
            );
        }
    }

    get htmlEntriesPM() {
        const transformed = this.transformedPmPhases;
        if (transformed && transformed.length > 0) {
            return PrineConvert.export(transformed).using(
                new PrineConvertAdapterHTML({
                    columnStyles: this.columnStyles(this.pmPhaseFields),
                })
            );
        }
    }

    get htmlEntriesXProcess() {
        const transformed = this.transformedXProcesses;
        if (transformed && transformed.length > 0) {
            return PrineConvert.export(transformed).using(
                new PrineConvertAdapterHTML({
                    columnStyles: this.columnStyles(this.xProcessFields),
                })
            );
        }
    }

    get tradeFields(): ExportField[] {
        return [
            {
                field: 'trade',
                enabled: true,
                default: true,
                display: false,
                render: false,
                header: (data) => 'Gewerke: ' + data.trade.title,
                data: () => '',
                width: '0%',
            },
            {
                field: 'insight',
                enabled: true,
                default: true,
                data: (data) => data.appliedLesson.lesson.insight,
                width: '20%',
            },
            {
                field: 'phase',
                enabled: true,
                default: true,
                data: (data) => data.phase.title,
                width: '8%',
            },
            {
                field: 'todo',
                enabled: false,
                data: (data) => data.appliedLesson.todo,
                width: '8%',
            },
            {
                field: 'solution',
                enabled: false,
                data: (data) => data.appliedLesson.solution,
                width: '8%',
            },
            {
                field: 'responsible',
                enabled: false,
                width: '8%',
                data: (data) => {
                    const user = findCombinedUser(data.users, data.appliedLesson);

                    if (user) {
                        return `${user.firstname} ${user.lastname}`;
                    }

                    return '-';
                },
            },
            {
                field: 'contacttill',
                enabled: false,
                data: (data) =>
                    data.appliedLesson.contacttill ? moment(data.appliedLesson.contacttill).format('DD.MM.YYYY') : '',
                ...Export.boolAttributes,
            },
            {
                field: 'done',
                enabled: false,
                default: false,
                data: (data) => data.appliedLesson.done,
                ...Export.boolAttributes,
            },
            {
                field: 'needscheck',
                enabled: false,
                default: false,
                data: (data) => data.appliedLesson.needscheck,
                ...Export.boolAttributes,
            },
            {
                field: 'checked',
                enabled: false,
                default: false,
                data: (data) => data.appliedLesson.checked,
                ...Export.boolAttributes,
            },
            {
                field: 'checked_by_user',
                enabled: false,
                default: false,
                data: (data) =>
                    data.appliedLesson.checked_by_user
                        ? `${data.appliedLesson.checked_by_user.firstname} ${data.appliedLesson.checked_by_user.lastname}`
                        : '',
                ...Export.boolAttributes,
            },
            {
                field: 'dynamic_field',
                enabled: false,
                default: false,
                data: (data) =>
                    data.appliedLesson.dynamic_field ? this.renderDynamicField(data.appliedLesson.dynamic_field) : '',
                ...Export.boolAttributes,
            },
            {
                field: 'cost',
                enabled: false,
                data: (data) => {
                    return data.appliedLesson.cost == null
                        ? ''
                        : isNaN(Number(data.appliedLesson.cost))
                        ? '-'
                        : Number(data.appliedLesson.cost).toLocaleString('de-CH');
                },
            },
            {
                field: 'probability',
                enabled: false,
                data: (data) => {
                    const probability = Number(data.appliedLesson.probability);
                    return data.appliedLesson.probability == null
                        ? ''
                        : isNaN(probability)
                        ? '-'
                        : (probability / 100).toLocaleString('de-CH', {
                              style: 'percent',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 2,
                          });
                },
            },
            {
                field: 'impact',
                enabled: false,
                header: () => {
                    return this.$t('print.impact', {
                        divider: this.maxDynamicFieldValue(),
                    }) as string;
                },
                data: (data) => {
                    const impact = Number(data.appliedLesson.impact);
                    return data.appliedLesson.impact == null
                        ? ''
                        : isNaN(impact)
                        ? '-'
                        : impact.toLocaleString('de-CH');
                },
            },
        ];
    }
    get pmPhaseFields(): ExportField[] {
        return [
            {
                field: 'phase',
                enabled: true,
                default: true,
                display: false,
                render: false,
                header: (data) => `PM Phase: ${data.pmphase.title} > ${data.pmSubPhase.title}`,
                data: (data) => data.pmphase.title,
                width: '0%',
            },
            {
                field: 'insight',
                enabled: true,
                default: true,
                data: (data) => data.pmAppliedLesson.lesson.insight,
                width: '20%',
            },
            {
                field: 'todo',
                enabled: false,
                data: (data) => data.pmAppliedLesson.todo,
                width: '12%',
            },
            {
                field: 'solution',
                enabled: false,
                data: (data) => data.pmAppliedLesson.solution,
                width: '12%',
            },
            {
                field: 'responsible',
                enabled: true,
                data: (data) => {
                    const user = findCombinedUser(data.users, data.pmAppliedLesson);

                    if (user) {
                        return `${user.firstname} ${user.lastname}`;
                    }

                    return '-';
                },
                ...Export.boolAttributes,
            },
            {
                field: 'contacttill',
                enabled: false,
                data: (data) =>
                    data.pmAppliedLesson.contacttill
                        ? moment(data.pmAppliedLesson.contacttill).format('DD.MM.YYYY')
                        : '',
                ...Export.boolAttributes,
            },
            {
                field: 'done',
                enabled: true,
                default: true,
                data: (data) => data.pmAppliedLesson.done,
                ...Export.boolAttributes,
            },
            {
                field: 'needscheck',
                enabled: false,
                default: false,
                data: (data) => data.pmAppliedLesson.needscheck,
                ...Export.boolAttributes,
            },
            {
                field: 'checked',
                enabled: false,
                default: false,
                data: (data) => data.pmAppliedLesson.checked,
                ...Export.boolAttributes,
            },
            {
                field: 'checked_by_user',
                enabled: false,
                default: false,
                data: (data) =>
                    data.pmAppliedLesson.checked_by_user
                        ? `${data.pmAppliedLesson.checked_by_user.firstname} ${data.pmAppliedLesson.checked_by_user.lastname}`
                        : '',
                ...Export.boolAttributes,
            },
            {
                field: 'dynamic_field',
                enabled: true,
                data: (data) =>
                    data.pmAppliedLesson.dynamic_field
                        ? this.renderDynamicField(data.pmAppliedLesson.dynamic_field)
                        : '',
                ...Export.boolAttributes,
            },
            {
                field: 'cost',
                enabled: false,
                data: (data) => {
                    return data.pmAppliedLesson.cost == null
                        ? ''
                        : isNaN(Number(data.pmAppliedLesson.cost))
                        ? '-'
                        : Number(data.pmAppliedLesson.cost).toLocaleString('de-CH');
                },
            },
            {
                field: 'probability',
                enabled: false,
                data: (data) => {
                    const probability = Number(data.pmAppliedLesson.probability);
                    return data.pmAppliedLesson.probability == null
                        ? ''
                        : isNaN(probability)
                        ? '-'
                        : (probability / 100).toLocaleString('de-CH', {
                              style: 'percent',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 2,
                          });
                },
            },
            {
                field: 'impact',
                enabled: false,
                header: () => {
                    return this.$t('print.impact', {
                        divider: this.maxDynamicFieldValue(),
                    }) as string;
                },
                data: (data) => {
                    const impact = Number(data.pmAppliedLesson.impact);
                    return data.pmAppliedLesson.impact == null
                        ? ''
                        : isNaN(impact)
                        ? '-'
                        : impact.toLocaleString('de-CH');
                },
            },
        ];
    }
    get xProcessFields(): ExportField[] {
        return [
            {
                field: 'xprocess',
                enabled: true,
                default: true,
                display: false,
                render: false,
                header: (data) => 'Prozess: ' + data.x_process.title,
                data: (data) => data.x_process.title,
                width: '0%',
            },
            {
                field: 'insight',
                enabled: true,
                default: true,
                data: (data) => data.x_applied_lessons.lesson.insight,
                width: '20%',
            },
            {
                field: 'todo',
                enabled: true,
                data: (data) => data.x_applied_lessons.todo,
                width: '12%',
            },
            {
                field: 'solution',
                enabled: true,
                data: (data) => data.x_applied_lessons.solution,
                width: '12%',
            },
            {
                field: 'responsible',
                enabled: true,
                data: (data) => {
                    const user = findCombinedUser(data.users, data.x_applied_lessons);

                    if (user) {
                        return `${user.firstname} ${user.lastname}`;
                    }

                    return '-';
                },
                ...Export.boolAttributes,
            },
            {
                field: 'contacttill',
                enabled: true,
                data: (data) =>
                    data.x_applied_lessons.contacttill
                        ? moment(data.x_applied_lessons.contacttill).format('DD.MM.YYYY')
                        : '',
            },
            {
                field: 'done',
                enabled: true,
                default: true,
                data: (data) => data.x_applied_lessons.done,
                ...Export.boolAttributes,
            },
            {
                field: 'needscheck',
                enabled: false,
                default: false,
                data: (data) => data.x_applied_lessons.needscheck,
                ...Export.boolAttributes,
            },
            {
                field: 'checked',
                enabled: false,
                default: false,
                data: (data) => data.x_applied_lessons.checked,
                ...Export.boolAttributes,
            },
            {
                field: 'checked_by_user',
                enabled: false,
                default: false,
                ...Export.boolAttributes,
                data: (data) =>
                    data.x_applied_lessons.checked_by_user
                        ? `${data.x_applied_lessons.checked_by_user.firstname} ${data.x_applied_lessons.checked_by_user.lastname}`
                        : '',
            },
            {
                field: 'dynamic_field',
                enabled: false,
                data: (data) =>
                    data.x_applied_lessons.dynamic_field
                        ? this.renderDynamicField(data.x_applied_lessons.dynamic_field)
                        : '',
                ...Export.boolAttributes,
            },
            {
                field: 'cost',
                enabled: false,
                data: (data) => {
                    return data.x_applied_lessons.cost == null
                        ? ''
                        : isNaN(Number(data.x_applied_lessons.cost))
                        ? '-'
                        : Number(data.x_applied_lessons.cost).toLocaleString('de-CH');
                },
            },
            {
                field: 'probability',
                enabled: false,
                data: (data) => {
                    const probability = Number(data.x_applied_lessons.probability);
                    return data.x_applied_lessons.probability == null
                        ? ''
                        : isNaN(probability)
                        ? '-'
                        : (probability / 100).toLocaleString('de-CH', {
                              style: 'percent',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 2,
                          });
                },
            },
            {
                field: 'impact',
                enabled: false,
                header: () => {
                    return this.$t('print.impact', {
                        divider: this.maxDynamicFieldValue(),
                    }) as string;
                },
                data: (data) => {
                    const impact = Number(data.x_applied_lessons.impact);
                    return data.x_applied_lessons.impact == null
                        ? ''
                        : isNaN(impact)
                        ? '-'
                        : impact.toLocaleString('de-CH');
                },
            },
        ];
    }

    private projectId: number;

    private tradeIds: number[] = [];
    private phaseIds: number[] = [];
    private topicIds: number[] = [];

    private pmPhaseIds: number[] = [];
    private pmSubPhasesIds: number[] = [];

    private xProcessIds: number[] = [];
    private xSubProcessesIds: number[] = [];

    private filterResolved: ResolvedEnum = ResolvedEnum.Resolved;
    private filterChecked: CheckedEnum = CheckedEnum.All;
    private dynamicFields: string[] = [];

    private selectedTabs: string[];

    private loaded = false;

    @Action('project/fetchAllProjects') private fetchAllProjects;
    @Getter('project/projects') private projects: Project[];
    @Getter('company/dynamicFields') private dynamicFieldModels: DynamicField[];
    @Getter('account/company') private company: Company;

    private project: Project | undefined = undefined;
    private topicsSelected: Topic[] = [];
    private pmSubPhases: PMSubPhase[] = [];
    private pmSubPhasesSelected: PMSubPhase[] = [];

    mounted() {
        if (this.$route.query.projectId) {
            this.projectId = parseInt(this.$route.query.projectId.toString());
            this.loadProject();
        }
    }

    refreshParams() {
        if (this.$route.query.selectedTabs) {
            this.selectedTabs = this.$route.query.selectedTabs as string[];
        }

        if (this.$route.query.tradeIds) {
            this.tradeIds = this.$route.query.tradeIds
                .toString()
                .split(',')
                .map((el) => parseInt(el)) as number[];
        }

        if (this.$route.query.phasesIds) {
            this.phaseIds = this.$route.query.phasesIds
                .toString()
                .split(',')
                .map((el) => parseInt(el)) as number[];
        }
        console.log('rr', this.$route.query, this.phaseIds);

        if (this.$route.query.topicIds) {
            this.topicIds = this.$route.query.topicIds
                .toString()
                .split(',')
                .map((el) => parseInt(el)) as number[];
        }

        if (this.$route.query.pmPhasesIds) {
            this.pmPhaseIds = this.$route.query.pmPhasesIds
                .toString()
                .split(',')
                .map((el) => parseInt(el)) as number[];
        }

        if (this.$route.query.pmSubPhasesIds) {
            this.pmSubPhasesIds = this.$route.query.pmSubPhasesIds
                .toString()
                .split(',')
                .map((el) => parseInt(el)) as number[];
        }

        if (this.$route.query.xProcessIds) {
            this.xProcessIds = this.$route.query.xProcessIds
                .toString()
                .split(',')
                .map((el) => parseInt(el)) as number[];
        }

        if (this.$route.query.xSubProcessesIds) {
            this.xSubProcessesIds = this.$route.query.xSubProcessesIds
                .toString()
                .split(',')
                .map((el) => parseInt(el)) as number[];
        }

        if (this.$route.query.filterResolved) {
            switch (this.$route.query.filterResolved) {
                case ResolvedEnum.Resolved:
                    this.filterResolved = ResolvedEnum.Resolved;
                    break;
                case ResolvedEnum.NotResolved:
                    this.filterResolved = ResolvedEnum.NotResolved;
                    break;
                case ResolvedEnum.All:
                    this.filterResolved = ResolvedEnum.All;
                    break;
            }
        }

        if (this.$route.query.filterChecked) {
            switch (this.$route.query.filterChecked) {
                case CheckedEnum.All:
                    this.filterChecked = CheckedEnum.All;
                    break;
                case CheckedEnum.NeedsCheck:
                    this.filterChecked = CheckedEnum.NeedsCheck;
                    break;
                case CheckedEnum.Checked:
                    this.filterChecked = CheckedEnum.Checked;
                    break;
                case CheckedEnum.NotChecked:
                    this.filterChecked = CheckedEnum.NotChecked;
                    break;
            }
        }

        this.parseQueryFields(this.tradeFields);
        this.parseQueryFields(this.pmPhaseFields);
        this.parseQueryFields(this.xProcessFields);

        if (this.companySetting.feature_dynamic_worksheet_field) {
            if (this.$route.query.dynamicFields) {
                this.dynamicFields = this.$route.query.dynamicFields
                    .toString()
                    .split(',')
                    .map((dynamicField) => dynamicField) as string[];
            }

            // Find Dynamic Field and set the header to the field name that is stored in company settings
            const trade_dynamic_field = this.tradeFields.find((field) => field.field === 'dynamic_field');
            const pm_phase_dynamic_field = this.pmPhaseFields.find((field) => field.field === 'dynamic_field');
            const x_process_dynamic_field = this.xProcessFields.find((field) => field.field === 'dynamic_field');

            if (trade_dynamic_field) {
                trade_dynamic_field.header = () => this.companySetting.dynamic_worksheet_field_name + '<br>(R)';
            }
            if (pm_phase_dynamic_field) {
                pm_phase_dynamic_field.header = () => this.companySetting.dynamic_worksheet_field_name + '<br>(R)';
            }
            if (x_process_dynamic_field) {
                x_process_dynamic_field.header = () => this.companySetting.dynamic_worksheet_field_name + '<br>(R)';
            }
        }

        if (this.project && this.project.title) {
            document.title = 'Overview Report ' + this.project.title;
        }

        const promises: Array<Promise<any>> = [];
        if (this.selectedTabs.includes('trades')) {
            promises.push(this.loadTopics());
        }

        if (this.selectedTabs.includes('pm')) {
            promises.push(this.loadPmSubPhases());
        }

        if (promises.length > 0) {
            Promise.all(promises).then(() => {
                this.loaded = true;
            });
        } else {
            this.loaded = true;
        }
    }

    maxDynamicFieldValue() {
        const dynamicFieldValues = this.dynamicFieldModels.map((df) => df.value).filter((v): v is number => v !== null);
        return dynamicFieldValues.length ? Math.max(...dynamicFieldValues) : 0;
    }

    reducedActiveFields(fields: ExportField[]) {
        return fields.filter((f) => f.enabled);
    }

    getTableTitleTrade(index) {
        return this.transformedTrades[index].fields[0];
    }

    getTableTitlePM(index) {
        return this.transformedPmPhases[index].fields[0];
    }

    getTableTitleXProcess(index) {
        return this.transformedXProcesses[index].fields[0];
    }

    protected hasMatchingDynamicField(lesson) {
        let display = false;
        if (
            this.dynamicFields.length === 0 ||
            (lesson.dynamic_field && this.dynamicFields.some((df: string) => df === lesson.dynamic_field.title))
        ) {
            display = true;
        }

        if (lesson.sub_lessons) {
            lesson.sub_lessons = lesson.sub_lessons.filter((subLesson) => this.hasMatchingDynamicField(subLesson));
        }
        return display || (lesson.sub_lessons && lesson.sub_lessons.length > 0);
    }

    private async loadTopics() {
        return topicWebservice
            .load()
            .then((response) => {
                let topics = response.data.filter((topic: Topic) => {
                    return topic.title !== 'Nicht zugeordnete Lehren';
                });
                topics = topics.sort((a: Topic, b: Topic) => {
                    return a.title.localeCompare(b.title);
                });

                this.topicsSelected = topics.filter((topic) => this.topicIds.includes(topic.id));
            })
            .catch(() => {
                showErrorNotification('Error in loading Topics');
            });
    }

    private async loadPmSubPhases() {
        return pmSubPhaseWebservice
            .loadAll()
            .then((response) => {
                const pmSubPhases = response.data.sort((a: PMSubPhase, b: PMSubPhase) => {
                    return a && b && a.order && b.order ? a.order - b.order : 0;
                });

                this.pmSubPhases = pmSubPhases;
                this.pmSubPhasesSelected = pmSubPhases.filter((topic) => this.pmSubPhasesIds.includes(topic.id));
            })
            .catch(() => {
                showErrorNotification('Error in loading Topics');
            });
    }

    private loadProject() {
        const project = this.projects.find((_) => _.id === this.projectId);

        if (project) {
            projectWebservice
                .loadOneByUser(this.projectId)
                .then((response) => {
                    this.project = response.data;
                    this.refreshParams();
                })
                .catch((error) => {
                    console.error(error);
                });
        }
    }

    private flatMap(applied_lessons: any, flattened: any[] = [], levels: any[] = []) {
        for (let index = 0; index < applied_lessons.length; index++) {
            const applied_lesson = applied_lessons[index];
            const itemLevels = [...levels, index + 1];
            applied_lesson.lesson.insight = itemLevels.join('.') + ' - ' + applied_lesson.lesson.insight;
            flattened.push(applied_lesson);
            if (applied_lesson.sub_lessons) {
                this.flatMap(applied_lesson.sub_lessons, flattened, itemLevels);
            }
        }
        return flattened;
    }
}
